/*
 * @Author: Teemo Ding
 * @Date: 2022-04-19 09:37:13
 * @LastEditTime: 2022-06-28 17:44:17
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Analytics/ReportSubscription/ReportSubscriptionDetail/index.tsx
 */
import CommonHeader from '@/src/components/CommonHeader';
import {
  createReportSubscription,
  initReportSubscription,
  reportSubscriptionDelete,
  reportSubscriptionSave,
} from '@/src/request/analytics/reportSubscription';
import { NEW } from '@/src/tools/constant';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, Spin, message } from 'antd';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import Reports from './Reports';
import ReportsFormat from './ReportsFormat';
import SendTo from './SendTo';
import SubscriptionName from './SubscriptionName';
import { breadcrumbData } from './config';
import css from './css.module.less';
import { ELang } from '@/src/types/common/language';
import { handleOnFormatStoreTimeAndDateWithTimezone } from '@/src/tools/tools';

const permissions = () => {
  const view_report_subscription_details_page = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.view_report_subscription_details_page,
      }),
    ],
  });
  const add_edit_report_subscription = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.add_edit_report_subscription,
      }),
    ],
  });
  const delete_report_subscription = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.delete_report_subscription,
      }),
    ],
  });
  return {
    view_report_subscription_details_page,
    add_edit_report_subscription,
    delete_report_subscription,
  };
};
const ReportSubscriptionDetail = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    view_report_subscription_details_page,
    add_edit_report_subscription,
    delete_report_subscription,
  } = permissions();
  const history = useHistory();
  const [form] = Form.useForm();
  const [reportSubscriptionName, setReportSubscriptionName] = useState('');
  const { reportSubscriptionUuid } = useParams() as any;

  const [hasModified, setHasModified] = useState(
    reportSubscriptionUuid === NEW ? true : false,
  );
  const [nextSendTime, setNextSendTime] = useState('');
  const [lastSentTime, setLastSentTime] = useState('');
  const [timeZone, setTimeZone] = useState('');

  const { loading } = useRequest(() => createReportSubscription(), {
    ready:
      reportSubscriptionUuid === NEW && !!view_report_subscription_details_page,
    onSuccess: ({ name, timeZone }) => {
      setReportSubscriptionName(name);
      form.setFieldsValue({
        name,
        reportUuids: [''],
        templateLang: ELang['en-GB'],
      });
      setTimeZone(timeZone);
    },
  });
  const { loading: initLoading } = useRequest(
    () => initReportSubscription(reportSubscriptionUuid),
    {
      ready:
        reportSubscriptionUuid !== NEW &&
        !!view_report_subscription_details_page,
      onSuccess: (res) => {
        const { name, timeZone, nextSend, lastSent } = res;
        setReportSubscriptionName(name);
        form.setFieldsValue({ ...res });
        setNextSendTime(
          nextSend ? handleOnFormatStoreTimeAndDateWithTimezone(nextSend) : '',
        );
        setLastSentTime(
          lastSent ? handleOnFormatStoreTimeAndDateWithTimezone(lastSent) : '',
        );
        setTimeZone(timeZone);
      },
    },
  );
  const { run: _reportSubscriptionSave } = useRequest(
    (data) => reportSubscriptionSave(data),
    {
      manual: true,
      onSuccess: (res) => {
        message.success(
          getI18n('reportSubscription.reportSubscriptionDetail.saved_msg'),
        );
        setHasModified(false);
        if (res.uuid) {
          history.replace(`/reportSubscription/${res.uuid}`);
        }
      },
    },
  );

  /**删除Report*/
  const { run: _reportSubscriptionDelete } = useRequest(
    () => reportSubscriptionDelete({ uuids: [reportSubscriptionUuid] }),
    {
      manual: true,
      onSuccess: (res) => {
        message.success(
          getI18n('reportSubscription.reportSubscriptionDetail.delete_msg'),
        );
        history.push(`/reportSubscriptionList`);
      },
    },
  );

  const beforeGoBack = () => {
    history.push('/reportSubscriptionList');
  };

  const onFinish = (values) => {
    if (values.email) {
      values.emails.push(values.email);
      form.setFieldsValue({ email: '' });
    }
    values.uuid = reportSubscriptionUuid === NEW ? '' : reportSubscriptionUuid;
    _reportSubscriptionSave(values);
  };
  const onValuesChange = () => {
    setHasModified(true);
  };

  return (
    <Spin spinning={loading || initLoading}>
      <CommonHeader
        breadcrumbData={breadcrumbData(reportSubscriptionName, { getI18n })}
        onGoBackClick={beforeGoBack}
        onDiscard={beforeGoBack}
        onSaveDisable={!hasModified || !add_edit_report_subscription}
        onSubmit={() => form.submit()}
        {...(reportSubscriptionUuid !== NEW && {
          onDelete: _reportSubscriptionDelete,
          disableDelete: !delete_report_subscription,
        })}
      />
      <Form
        className={css.reportSubscriptionDetail}
        form={form}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
        name='reportSubscriptionDetail'>
        <SubscriptionName
          reportSubscriptionName={reportSubscriptionName}
          disabled={!add_edit_report_subscription}
        />
        <Reports disabled={!add_edit_report_subscription} />
        <ReportsFormat disabled={!add_edit_report_subscription} />
        <SendTo
          form={form}
          timeZone={timeZone}
          nextSendTime={nextSendTime}
          lastSentTime={lastSentTime}
          setNextSendTime={setNextSendTime}
          disabled={!add_edit_report_subscription}
        />
      </Form>
    </Spin>
  );
};

export default ReportSubscriptionDetail;
